import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, RotateCcw } from 'lucide-react';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

type AspectOption = { label: string; ratio: number | null };

export const ASPECT_OPTIONS: AspectOption[] = [
  { label: 'Libre', ratio: null },
  { label: '1:1', ratio: 1 },
  { label: '4:5', ratio: 4 / 5 },
  { label: '9:16', ratio: 9 / 16 },
  { label: '16:9', ratio: 16 / 9 },
  { label: '2:3', ratio: 2 / 3 },
];

// Hook that encapsulates all crop state and logic
/** Zona de agarre de bordes y esquinas, en px de pantalla, de cada lado de la línea
 *  (ADR-2026-10-07-el-recorte-se-agarra-facil). Antes eran 14 y, con el lienzo del tamaño
 *  exacto de la imagen, la mitad quedaba afuera. */
const TOLERANCIA_BORDE = 18;
const TOLERANCIA_ESQUINA = 24;
/** `zoomAlSoltar`: cuánto se espera quieto, cuánto dura el acercamiento, qué parte del
 *  lienzo ocupa el recorte al terminar y hasta cuánto se acerca sobre la imagen entera
 *  (ADR-2026-10-20-el-recorte-hace-zoom-y-el-fondo-se-quita-del-recorte). */
const ESPERA_DEL_ZOOM_MS = 1500;
const DURACION_DEL_ZOOM_MS = 900;
const RELLENO_DEL_ZOOM = 0.78;
/** Sutil: como mucho 1,5× la imagen entera, y nunca más grande que su tamaño real (un
 *  píxel de la imagen por punto): agrandar más sólo muestra píxeles estirados
 *  (ADR-2026-10-20-el-recorte-no-se-mueve-al-quitar-el-fondo). */
const ZOOM_MAXIMO = 1.5;

export function useCropState(
  imageUrl: string,
  fileName: string,
  onDone: () => void,
  /** Aire alrededor de la imagen dentro del lienzo, en px: deja agarrar bordes y esquinas
   *  cuando el recorte llega al límite. El visor lo usa con el lienzo agrandado en la
   *  misma medida; sin la prop, 0, como siempre. */
  opciones: {
    margen?: number;
    /** Al cambiar el tamaño de la ventana el recorte se mantiene, en proporción, en vez
     *  de volver a la imagen entera, y un reacomodo sin cambios no deshace el zoom. Lo usa
     *  la preparación de un elemento de marca
     *  (ADR-2026-10-20-el-elemento-se-prepara-antes-de-subir). Sin la prop, como siempre. */
    conservarRecorte?: boolean;
    /** Al soltar un borde y quedarse quieto un momento, la vista se acerca de a poco hasta
     *  que el recorte ocupe casi todo el lienzo, como el recorte de las fotos del teléfono
     *  (ADR-2026-10-20-el-recorte-hace-zoom-y-el-fondo-se-quita-del-recorte). Sin la prop,
     *  la vista queda fija, como siempre. */
    zoomAlSoltar?: boolean;
    /** El lienzo se dibuja a la densidad de la pantalla (`devicePixelRatio`): en una
     *  Retina, el doble de píxeles. Sin la prop, un píxel por punto, como siempre. */
    nitido?: boolean;
    /** El sombreado de afuera del recorte se pinta sólo sobre la imagen, no sobre el
     *  margen: el visor del teléfono pone el lienzo en toda el área de la imagen, y con el
     *  margen sombreado se veía como una segunda capa
     *  (ADR-2026-10-28-el-recorte-del-telefono-entra-en-su-area). Sin la prop, como siempre. */
    sombraSoloEnLaImagen?: boolean;
  } = {},
) {
  const margen = Math.max(0, opciones.margen ?? 0);
  const conservarRecorte = !!opciones.conservarRecorte;
  const zoomAlSoltar = !!opciones.zoomAlSoltar;
  const nitido = !!opciones.nitido;
  const sombraSoloEnLaImagen = !!opciones.sombraSoloEnLaImagen;
  /** El último recorte como fracción de la imagen (0–1), para `conservarRecorte`. */
  const recorteRelativoRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  /** La imagen del último layout. */
  const ultimaImagenRef = useRef<string | null>(null);
  /** La escala con la que la imagen entera entra en el lienzo: el zoom nunca baja de ahí. */
  const escalaBaseRef = useRef(1);
  /** `zoomAlSoltar`: la espera antes de acercar y el cuadro de la animación en curso. */
  const esperaDeZoomRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animacionDeZoomRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  // Track when the container DOM node is attached (state triggers re-render / effect re-run)
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [selectedAspect, setSelectedAspect] = useState<number>(0);
  const [crop, setCrop] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; crop: CropArea }>({ x: 0, y: 0, crop: { x: 0, y: 0, width: 0, height: 0 } });
  const [displayScale, setDisplayScale] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  /** La vista del último render, para lo que corre fuera de él (el zoom, el layout). */
  const vistaRef = useRef({ scale: displayScale, offset: imageOffset, crop });
  vistaRef.current = { scale: displayScale, offset: imageOffset, crop };
  /** Las medidas de la imagen del último layout. */
  const ultimasMedidasRef = useRef<{ w: number; h: number } | null>(null);
  /** El tamaño del lienzo en el último layout. */
  const ultimoLienzoRef = useRef<{ w: number; h: number } | null>(null);

  // Callback ref: when the canvas component mounts, it calls this to register the container
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerNodeRef.current = node;
    setContainerNode(node);
  }, []);

  // Load image
  useEffect(() => {
    setImageLoaded(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
    };
    img.onerror = () => console.error('Failed to load image for cropping');
    img.src = imageUrl;
  }, [imageUrl]);

  const calculateLayout = useCallback(() => {
    if (!imageRef.current || !containerNodeRef.current) return;
    const container = containerNodeRef.current;
    const img = imageRef.current;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    if (cw === 0 || ch === 0) return; // container not yet laid out
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const medidas = ultimasMedidasRef.current;
    const lienzo = ultimoLienzoRef.current;
    // Misma imagen y mismo lienzo (el ResizeObserver avisa al suscribirse): nada que
    // reacomodar, y reacomodar desharía el zoom.
    if (
      conservarRecorte &&
      ultimaImagenRef.current === img.src &&
      medidas?.w === iw &&
      medidas?.h === ih &&
      lienzo?.w === cw &&
      lienzo?.h === ch
    ) {
      return;
    }
    ultimasMedidasRef.current = { w: iw, h: ih };
    ultimoLienzoRef.current = { w: cw, h: ch };
    // La imagen entra en el lienzo menos el margen de cada lado.
    const scale = Math.min((cw - 2 * margen) / iw, (ch - 2 * margen) / ih, 1);
    const dw = iw * scale;
    const dh = ih * scale;
    const ox = (cw - dw) / 2;
    const oy = (ch - dh) / 2;
    if (esperaDeZoomRef.current) clearTimeout(esperaDeZoomRef.current);
    if (animacionDeZoomRef.current !== null) cancelAnimationFrame(animacionDeZoomRef.current);
    escalaBaseRef.current = scale;
    setDisplayScale(scale);
    setImageOffset({ x: ox, y: oy });
    setDisplaySize({ width: dw, height: dh });
    const misma = ultimaImagenRef.current === img.src;
    ultimaImagenRef.current = img.src;
    const previo = conservarRecorte && misma ? recorteRelativoRef.current : null;
    setCrop(
      previo
        ? { x: previo.x * dw, y: previo.y * dh, width: previo.w * dw, height: previo.h * dh }
        : { x: 0, y: 0, width: dw, height: dh },
    );
  }, [margen, conservarRecorte]);

  useEffect(() => {
    if (!conservarRecorte || displaySize.width <= 0 || displaySize.height <= 0) return;
    const f = (v: number) => Math.min(1, Math.max(0, v));
    recorteRelativoRef.current = {
      x: f(crop.x / displaySize.width),
      y: f(crop.y / displaySize.height),
      w: f(crop.width / displaySize.width),
      h: f(crop.height / displaySize.height),
    };
  }, [conservarRecorte, crop, displaySize]);

  // Recalculate when image loads or container appears (retry a few frames for layout)
  useEffect(() => {
    if (!imageLoaded || !containerNode) return;
    let cancelled = false;
    let retries = 0;
    const tryLayout = () => {
      if (cancelled) return;
      calculateLayout();
      // Retry up to 5 frames in case container isn't laid out yet
      if (retries < 5 && containerNode.clientWidth === 0) {
        retries++;
        requestAnimationFrame(tryLayout);
      }
    };
    requestAnimationFrame(tryLayout);
    return () => { cancelled = true; };
  }, [imageLoaded, containerNode, calculateLayout]);

  // ResizeObserver: recalculate when container gets actual dimensions
  useEffect(() => {
    if (!containerNode || !imageLoaded) return;
    const ro = new ResizeObserver(() => calculateLayout());
    ro.observe(containerNode);
    return () => ro.disconnect();
  }, [containerNode, imageLoaded, calculateLayout]);

  useEffect(() => {
    const handleResize = () => { if (imageLoaded) calculateLayout(); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageLoaded, calculateLayout]);

  // Draw canvas
  useEffect(() => {
    if (!canvasRef.current || !imageRef.current || !imageLoaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const container = containerNodeRef.current;
    if (!container) return;
    // Todo se dibuja en puntos de pantalla (W × H); con `nitido`, el lienzo tiene
    // devicePixelRatio píxeles por punto y la transformación los reparte.
    const W = container.clientWidth;
    const H = container.clientHeight;
    const densidad = nitido ? Math.max(1, window.devicePixelRatio || 1) : 1;
    canvas.width = Math.round(W * densidad);
    canvas.height = Math.round(H * densidad);
    if (densidad !== 1) {
      ctx.setTransform(densidad, 0, 0, densidad, 0, 0);
      ctx.imageSmoothingQuality = 'high';
    }

    ctx.drawImage(imageRef.current, imageOffset.x, imageOffset.y, displaySize.width, displaySize.height);

    // Dark overlay outside crop
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    if (sombraSoloEnLaImagen) {
      const { x: ix, y: iy } = imageOffset;
      const { width: iw, height: ih } = displaySize;
      ctx.fillRect(ix, iy, iw, crop.y);
      ctx.fillRect(ix, iy + crop.y + crop.height, iw, ih - (crop.y + crop.height));
      ctx.fillRect(ix, iy + crop.y, crop.x, crop.height);
      ctx.fillRect(ix + crop.x + crop.width, iy + crop.y, iw - (crop.x + crop.width), crop.height);
    } else {
      ctx.fillRect(0, 0, W, imageOffset.y + crop.y);
      ctx.fillRect(0, imageOffset.y + crop.y + crop.height, W, H - (imageOffset.y + crop.y + crop.height));
      ctx.fillRect(0, imageOffset.y + crop.y, imageOffset.x + crop.x, crop.height);
      ctx.fillRect(imageOffset.x + crop.x + crop.width, imageOffset.y + crop.y, W - (imageOffset.x + crop.x + crop.width), crop.height);
    }

    // Crop border
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.strokeRect(imageOffset.x + crop.x, imageOffset.y + crop.y, crop.width, crop.height);

    // Grid lines (rule of thirds)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 0.5;
    const cx = imageOffset.x + crop.x;
    const cy = imageOffset.y + crop.y;
    for (let i = 1; i <= 2; i++) {
      ctx.beginPath(); ctx.moveTo(cx + (crop.width * i) / 3, cy); ctx.lineTo(cx + (crop.width * i) / 3, cy + crop.height); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx, cy + (crop.height * i) / 3); ctx.lineTo(cx + crop.width, cy + (crop.height * i) / 3); ctx.stroke();
    }

    // Barritas en el medio de cada borde: dicen que los bordes también se arrastran.
    const barra = 18;
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    const mx = cx + crop.width / 2;
    const my = cy + crop.height / 2;
    ctx.beginPath(); ctx.moveTo(mx - barra / 2, cy); ctx.lineTo(mx + barra / 2, cy); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(mx - barra / 2, cy + crop.height); ctx.lineTo(mx + barra / 2, cy + crop.height); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, my - barra / 2); ctx.lineTo(cx, my + barra / 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx + crop.width, my - barra / 2); ctx.lineTo(cx + crop.width, my + barra / 2); ctx.stroke();
    ctx.lineCap = 'butt';

    // Corner handles
    const handleSize = 20;
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 3;
    const corners = [
      { x: cx, y: cy },
      { x: cx + crop.width, y: cy },
      { x: cx, y: cy + crop.height },
      { x: cx + crop.width, y: cy + crop.height },
    ];
    corners.forEach(({ x, y }, idx) => {
      const ddx = idx % 2 === 0 ? 1 : -1;
      const ddy = idx < 2 ? 1 : -1;
      ctx.beginPath(); ctx.moveTo(x, y + ddy * handleSize); ctx.lineTo(x, y); ctx.lineTo(x + ddx * handleSize, y); ctx.stroke();
    });
  }, [crop, imageOffset, displaySize, imageLoaded, containerNode, nitido]);

  const getPos = (e: { clientX: number; clientY: number }): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const getHitZone = (pos: { x: number; y: number }): string | null => {
    const cx = imageOffset.x + crop.x;
    const cy = imageOffset.y + crop.y;
    const dx = (v: number) => Math.abs(pos.x - v);
    const dy = (v: number) => Math.abs(pos.y - v);
    // Esquinas primero, con más tolerancia: son las que más se usan.
    const cerca = (x: number, y: number) => dx(x) < TOLERANCIA_ESQUINA && dy(y) < TOLERANCIA_ESQUINA;
    if (cerca(cx, cy)) return 'nw';
    if (cerca(cx + crop.width, cy)) return 'ne';
    if (cerca(cx, cy + crop.height)) return 'sw';
    if (cerca(cx + crop.width, cy + crop.height)) return 'se';
    // Bordes: cerca de la línea y dentro de su largo.
    const enX = pos.x > cx - TOLERANCIA_BORDE && pos.x < cx + crop.width + TOLERANCIA_BORDE;
    const enY = pos.y > cy - TOLERANCIA_BORDE && pos.y < cy + crop.height + TOLERANCIA_BORDE;
    if (enX && dy(cy) < TOLERANCIA_BORDE) return 'n';
    if (enX && dy(cy + crop.height) < TOLERANCIA_BORDE) return 's';
    if (enY && dx(cx) < TOLERANCIA_BORDE) return 'w';
    if (enY && dx(cx + crop.width) < TOLERANCIA_BORDE) return 'e';
    if (pos.x > cx && pos.x < cx + crop.width && pos.y > cy && pos.y < cy + crop.height) return 'move';
    return null;
  };

  const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

  const applyAspectConstraint = (newCrop: CropArea, handle: string): CropArea => {
    const aspectOption = ASPECT_OPTIONS[selectedAspect];
    if (!aspectOption.ratio) return newCrop;
    const ratio = aspectOption.ratio;
    const c = { ...newCrop };
    if (handle === 'move') return c;
    if (handle === 'se' || handle === 'e' || handle === 's') {
      c.height = c.width / ratio;
      if (c.y + c.height > displaySize.height) { c.height = displaySize.height - c.y; c.width = c.height * ratio; }
    } else if (handle === 'sw' || handle === 'w') {
      c.height = c.width / ratio;
      if (c.y + c.height > displaySize.height) { c.height = displaySize.height - c.y; c.width = c.height * ratio; c.x = newCrop.x + newCrop.width - c.width; }
    } else if (handle === 'ne' || handle === 'n') {
      c.width = c.height * ratio;
      if (c.x + c.width > displaySize.width) { c.width = displaySize.width - c.x; c.height = c.width / ratio; c.y = newCrop.y + newCrop.height - c.height; }
    } else if (handle === 'nw') {
      c.height = c.width / ratio;
      if (c.x < 0) { c.x = 0; c.width = newCrop.x + newCrop.width; c.height = c.width / ratio; c.y = newCrop.y + newCrop.height - c.height; }
    }
    return c;
  };

  /** La zona bajo el puntero sin arrastrar: elige el cursor. */
  const [hover, setHover] = useState<string | null>(null);

  // ── Zoom al soltar (`zoomAlSoltar`) ──────────────────────────────────────────────────
  // Acercar es agrandar la imagen dibujada: `displayScale`, `imageOffset` y `displaySize`
  // cambian juntos y el recorte se reescala con ellos, así que el resto (dibujo, zonas de
  // agarre, topes, `recortarABlob`, `areaNormalizada`) sigue igual sin saber del zoom.
  const cancelarZoom = useCallback(() => {
    if (esperaDeZoomRef.current) clearTimeout(esperaDeZoomRef.current);
    if (animacionDeZoomRef.current !== null) cancelAnimationFrame(animacionDeZoomRef.current);
    esperaDeZoomRef.current = null;
    animacionDeZoomRef.current = null;
  }, []);
  useEffect(() => cancelarZoom, [cancelarZoom]);

  const encuadrar = useCallback(() => {
    const img = imageRef.current;
    const cont = containerNodeRef.current;
    const { scale: s0, offset: o0, crop: c0 } = vistaRef.current;
    if (!img || !cont || s0 <= 0 || c0.width <= 0 || c0.height <= 0) return;
    const cw = cont.clientWidth;
    const ch = cont.clientHeight;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    // El recorte en píxeles de la imagen: es lo que no cambia con el zoom.
    const r = { x: c0.x / s0, y: c0.y / s0, w: c0.width / s0, h: c0.height / s0 };
    const base = escalaBaseRef.current;
    const tope = Math.min(base * ZOOM_MAXIMO, Math.max(base, 1));
    const s1 = clamp(Math.min((cw * RELLENO_DEL_ZOOM) / r.w, (ch * RELLENO_DEL_ZOOM) / r.h), base, tope);
    // En la escala de base, la imagen entera centrada (el layout de siempre); acercado, el
    // recorte al centro del lienzo.
    const o1 =
      s1 <= base * 1.001
        ? { x: (cw - iw * s1) / 2, y: (ch - ih * s1) / 2 }
        : { x: cw / 2 - (r.x + r.w / 2) * s1, y: ch / 2 - (r.y + r.h / 2) * s1 };
    if (Math.abs(s1 - s0) < 0.001 && Math.abs(o1.x - o0.x) < 0.5 && Math.abs(o1.y - o0.y) < 0.5) return;
    const inicio = performance.now();
    const paso = (ahora: number) => {
      const t = Math.min(1, (ahora - inicio) / DURACION_DEL_ZOOM_MS);
      // Frena al final (ease-out cúbica): se acomoda, no salta.
      const e = 1 - Math.pow(1 - t, 3);
      const s = s0 + (s1 - s0) * e;
      setDisplayScale(s);
      setImageOffset({ x: o0.x + (o1.x - o0.x) * e, y: o0.y + (o1.y - o0.y) * e });
      setDisplaySize({ width: iw * s, height: ih * s });
      setCrop({ x: r.x * s, y: r.y * s, width: r.w * s, height: r.h * s });
      animacionDeZoomRef.current = t < 1 ? requestAnimationFrame(paso) : null;
    };
    animacionDeZoomRef.current = requestAnimationFrame(paso);
  }, []);

  const encuadrarEnUnRato = (ms: number) => {
    if (!zoomAlSoltar) return;
    cancelarZoom();
    esperaDeZoomRef.current = setTimeout(encuadrar, ms);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const pos = getPos(e);
    const zone = getHitZone(pos);
    if (!zone) return;
    cancelarZoom();
    // Captura: los movimientos y el soltar le siguen llegando al lienzo aunque el puntero
    // salga de él (o de la imagen). Antes, un píxel afuera soltaba el borde
    // (ADR-2026-10-07-el-recorte-se-agarra-facil).
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* sin captura sigue andando adentro del lienzo */
    }
    setDragging(zone);
    setDragStart({ x: pos.x, y: pos.y, crop: { ...crop } });
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragging) {
      setHover(getHitZone(getPos(e)));
      return;
    }
    e.preventDefault();
    const pos = getPos(e);
    const dx = pos.x - dragStart.x;
    const dy = pos.y - dragStart.y;
    const sc = dragStart.crop;
    const minSize = 30;
    let newCrop: CropArea;
    if (dragging === 'move') {
      newCrop = { x: clamp(sc.x + dx, 0, displaySize.width - sc.width), y: clamp(sc.y + dy, 0, displaySize.height - sc.height), width: sc.width, height: sc.height };
    } else {
      let x = sc.x, y = sc.y, w = sc.width, h = sc.height;
      if (dragging.includes('e')) { w = clamp(sc.width + dx, minSize, displaySize.width - sc.x); }
      if (dragging.includes('w')) { const newX = clamp(sc.x + dx, 0, sc.x + sc.width - minSize); w = sc.width + (sc.x - newX); x = newX; }
      if (dragging.includes('s')) { h = clamp(sc.height + dy, minSize, displaySize.height - sc.y); }
      if (dragging.includes('n')) { const newY = clamp(sc.y + dy, 0, sc.y + sc.height - minSize); h = sc.height + (sc.y - newY); y = newY; }
      newCrop = { x, y, width: w, height: h };
      newCrop = applyAspectConstraint(newCrop, dragging);
    }
    newCrop.x = clamp(newCrop.x, 0, displaySize.width - minSize);
    newCrop.y = clamp(newCrop.y, 0, displaySize.height - minSize);
    newCrop.width = clamp(newCrop.width, minSize, displaySize.width - newCrop.x);
    newCrop.height = clamp(newCrop.height, minSize, displaySize.height - newCrop.y);
    setCrop(newCrop);
  };

  const handlePointerUp = (e?: React.PointerEvent<HTMLCanvasElement>) => {
    if (e) {
      try {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* nada */
      }
    }
    if (dragging) encuadrarEnUnRato(ESPERA_DEL_ZOOM_MS);
    setDragging(null);
  };

  const handleAspectChange = (index: number) => {
    setSelectedAspect(index);
    const option = ASPECT_OPTIONS[index];
    if (!option.ratio) return;
    const ratio = option.ratio;
    let w: number, h: number;
    if (displaySize.width / displaySize.height > ratio) { h = displaySize.height; w = h * ratio; } else { w = displaySize.width; h = w / ratio; }
    setCrop({ x: (displaySize.width - w) / 2, y: (displaySize.height - h) / 2, width: w, height: h });
    encuadrarEnUnRato(0);
  };

  const handleReset = () => {
    setSelectedAspect(0);
    setCrop({ x: 0, y: 0, width: displaySize.width, height: displaySize.height });
    encuadrarEnUnRato(0);
  };

  const handleCropAndDownload = () => {
    if (!imageRef.current || displayScale === 0) return;
    const sx = crop.x / displayScale;
    const sy = crop.y / displayScale;
    const sw = crop.width / displayScale;
    const sh = crop.height / displayScale;
    const offscreen = document.createElement('canvas');
    offscreen.width = Math.round(sw);
    offscreen.height = Math.round(sh);
    const ctx = offscreen.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(imageRef.current, sx, sy, sw, sh, 0, 0, offscreen.width, offscreen.height);
    offscreen.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      // El recorte se rasteriza SIEMPRE como PNG (el `toBlob(..., 'image/png')` de
      // abajo), así que el nombre termina en `.png` aunque el original fuera `.jpg`.
      // Heredar la extensión del original hacía que el archivo mintiera sobre su
      // formato y Finder no le generara miniatura
      // (ADR-2026-09-08-descargas-con-extension-que-miente).
      const base = (fileName || 'imagen').replace(/\.[a-z0-9]{1,5}$/i, '') || 'imagen';
      const name = `${base}_recortada.png`;
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onDone();
    }, 'image/png');
  };

  /** El área elegida como fracción de la imagen (0–1): no depende de la resolución del
   *  archivo que se cargó, así el backend la aplica sobre el original
   *  (ADR-2026-10-06-panel-de-edicion-en-el-visor). */
  const areaNormalizada =
    displaySize.width > 0 && displaySize.height > 0
      ? {
          x: crop.x / displaySize.width,
          y: crop.y / displaySize.height,
          width: crop.width / displaySize.width,
          height: crop.height / displaySize.height,
        }
      : null;

  /** El recorte en PNG, sin descargarlo: para quien lo sube en vez de bajarlo. null si la
   *  imagen no cargó. */
  const recortarABlob = (): Promise<Blob | null> => {
    const img = imageRef.current;
    if (!img || displayScale === 0) return Promise.resolve(null);
    const sw = crop.width / displayScale;
    const sh = crop.height / displayScale;
    const lienzo = document.createElement('canvas');
    lienzo.width = Math.max(1, Math.round(sw));
    lienzo.height = Math.max(1, Math.round(sh));
    const ctx = lienzo.getContext('2d');
    if (!ctx) return Promise.resolve(null);
    ctx.drawImage(img, crop.x / displayScale, crop.y / displayScale, sw, sh, 0, 0, lienzo.width, lienzo.height);
    return new Promise((resolve) => lienzo.toBlob((b) => resolve(b), 'image/png'));
  };

  /** ¿El recorte es la imagen entera? (con un píxel de tolerancia de pantalla) */
  const recorteEntero =
    crop.x <= 1 && crop.y <= 1 && crop.width >= displaySize.width - 1 && crop.height >= displaySize.height - 1;

  const realWidth = displayScale > 0 ? Math.round(crop.width / displayScale) : 0;
  const realHeight = displayScale > 0 ? Math.round(crop.height / displayScale) : 0;

  return {
    canvasRef, setContainerRef, imageLoaded, selectedAspect, dragging, hover, realWidth, realHeight, areaNormalizada,
    handlePointerDown, handlePointerMove, handlePointerUp, handleAspectChange, handleReset, handleCropAndDownload,
    recortarABlob, recorteEntero,
  };
}

/**
 * El cursor para una zona del recorte, con los cursores de Atlas (`index.css`): flecha
 * afuera, manito sobre el recorte, flechas de redimensionar en bordes y esquinas. Antes eran
 * los del sistema (`default`, `crosshair`, `grabbing`), que pisaban el de la app
 * (ADR-2026-10-07-el-recorte-se-agarra-facil).
 */
export function cursorDeRecorte(zona: string | null, arrastrando: boolean): string {
  switch (zona) {
    case 'nw':
    case 'se':
      return 'var(--cursor-redimensionar-nwse)';
    case 'ne':
    case 'sw':
      return 'var(--cursor-redimensionar-nesw)';
    case 'n':
    case 's':
      return 'var(--cursor-redimensionar-ns)';
    case 'e':
    case 'w':
      return 'var(--cursor-redimensionar-ew)';
    case 'move':
      return arrastrando ? 'var(--cursor-mover)' : 'var(--cursor-manito)';
    default:
      return 'var(--cursor-flecha), auto';
  }
}

// Canvas component — goes where the expanded image normally is
export const ImageCropCanvas: React.FC<{
  cropState: ReturnType<typeof useCropState>;
  /** El fondo del lienzo. Sin la prop, el gris de siempre; el visor lo pide transparente
   *  porque el recorte va calzado sobre la misma imagen. */
  fondoClassName?: string;
}> = ({ cropState, fondoClassName = 'bg-[#F2F2F7]' }) => {
  const { setContainerRef, canvasRef, imageLoaded, dragging, hover, handlePointerDown, handlePointerMove, handlePointerUp } = cropState;

  return (
    <div ref={setContainerRef} className={`w-full h-full relative overflow-hidden touch-none ${fondoClassName}`}>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{ cursor: cursorDeRecorte(dragging ?? hover, !!dragging) }}
      />
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-brand-primary rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

// Controls component — aspect pills + dimensions + save button
// variant='mobile' replaces the 6 action buttons area
// variant='desktop' goes in the right panel
export const ImageCropControls: React.FC<{
  cropState: ReturnType<typeof useCropState>;
  onCancel: () => void;
  /**
   * Dónde vive el panel:
   *  - `mobile`: la fila de acciones del visor en el teléfono.
   *  - `desktop`: la tarjeta angosta (280–300px) que flota en el visor. Ahí las pills
   *    van al ancho que necesita cada una: repartidas, "Libre" no entra.
   *  - `panel`: el panel de configuración de un modo (400–600px), donde el recorte es
   *    LA pantalla — ADR-2026-09-07-el-recorte-se-hace-en-el-panel-del-modo. Sin título
   *    propio (lo dan el breadcrumb y el `<h1>` del shell), pills repartidas a todo el
   *    ancho y la resolución final como su propia sección, debajo de las proporciones
   *    (ADR-2026-09-07-el-panel-del-recorte-reparte-las-pills-y-baja-la-resolucion).
   */
  variant: 'mobile' | 'desktop' | 'panel';
}> = ({ cropState, onCancel, variant }) => {
  const { imageLoaded, selectedAspect, realWidth, realHeight, handleAspectChange, handleReset, handleCropAndDownload } = cropState;

  if (variant === 'mobile') {
    return (
      <div className="space-y-2">
        {/* Dimensions */}
        {imageLoaded && (
          <div className="flex items-center justify-center py-1">
            <span className="text-[13px] text-neutral-400">{realWidth} × {realHeight} px</span>
          </div>
        )}
        {/* Aspect ratio pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          {ASPECT_OPTIONS.map((option, i) => (
            <button
              key={option.label}
              type="button"
              onClick={() => handleAspectChange(i)}
              className={`flex-shrink-0 h-8 px-3 rounded-md text-[13px] font-medium transition-colors ${
                selectedAspect === i
                  ? 'bg-brand-primary text-white'
                  : 'bg-surface-card text-neutral-600 border border-neutral-200'
              }`}
            >
              {option.label}
            </button>
          ))}
          <button
            type="button"
            onClick={handleReset}
            className="flex-shrink-0 h-8 w-8 rounded-md bg-surface-card text-neutral-500 border border-neutral-200 flex items-center justify-center transition-colors hover:bg-surface-elevated"
            aria-label="Restablecer"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
        {/* Action buttons — solo "Descargar" full-width. El botón de "volver"
            de arriba hace de cancelar (cierra el recorte y vuelve a la imagen). */}
        <div className="pt-1">
          <button
            type="button"
            onClick={handleCropAndDownload}
            disabled={!imageLoaded}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-md text-[15px] font-medium bg-brand-primary text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-40"
          >
            <Download className="w-4 h-4" />
            Descargar
          </button>
        </div>
      </div>
    );
  }

  // Variante PANEL: el recorte montado en el panel de configuración de un modo.
  //
  // Se separa de `desktop` y no es un flag encima suyo porque las dos diferencias salen
  // del ANCHO: acá hay 400–600px y allá 280. Con ese aire, las proporciones se reparten
  // la fila entera —seis objetivos parejos, sin un hueco a la derecha— y la resolución
  // final se lee mejor DEBAJO de ellas: es el resultado de lo que se acaba de elegir, no
  // un dato previo. Los paddings son `px-6`, los del resto del panel, así que el bloque
  // alinea con el subtítulo de arriba.
  if (variant === 'panel') {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-3 flex flex-col gap-5">
          <div>
            <p className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide mb-2">
              Tamaño
            </p>
            {/* `flex-1 min-w-0` en cada pill: se reparten el ancho en partes iguales y
                el texto no las estira. El "Restablecer" queda fijo — es un ícono, no una
                opción más, y estirarlo lo haría competir con las que sí lo son. */}
            <div className="flex items-stretch gap-1.5">
              {ASPECT_OPTIONS.map((option, i) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => handleAspectChange(i)}
                  className={`flex-1 min-w-0 h-9 px-1 rounded-md text-[13px] font-medium transition-colors ${
                    selectedAspect === i
                      ? 'bg-brand-primary text-white'
                      : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'
                  }`}
                >
                  {option.label}
                </button>
              ))}
              <button
                type="button"
                onClick={handleReset}
                className="shrink-0 h-9 w-9 rounded-md bg-surface-card text-neutral-500 border border-neutral-200 flex items-center justify-center transition-colors hover:bg-surface-elevated"
                aria-label="Restablecer"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* La resolución del recorte, con su propio rótulo: es el número que se va a
              descargar, y sin nombre se leía como el tamaño del original. */}
          {imageLoaded && (
            <div>
              <p className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide mb-2">
                Resolución final
              </p>
              {/* Gris NEUTRO y no `bg-surface-elevated`: ese token es `rgb(247,248,251)`
                  —un blanco azulado— y sobre el blanco de la card se leía celeste, no
                  gris. `black/[0.06]` da un gris parejo y con contraste real contra el
                  fondo (ADR-2026-09-07-el-panel-del-recorte-reparte-las-pills-y-baja-la-resolucion). */}
              <div className="flex items-center justify-center py-2.5 rounded-md bg-black/[0.06]">
                <span className="text-[13px] font-medium text-neutral-600">
                  {realWidth} × {realHeight} px
                </span>
              </div>
            </div>
          )}
        </div>

        {/* El mismo pie que el de los otros modos: filete de 1px y el CTA a todo el
            ancho de la card. */}
        <div className="shrink-0 px-6 pt-3 pb-4 bg-white border-t border-black/[0.05] space-y-2">
          <button
            type="button"
            onClick={handleCropAndDownload}
            disabled={!imageLoaded}
            className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
          >
            <Download className="w-4 h-4" />
            Descargar
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="w-full flex items-center justify-center h-11 rounded-2xl text-[15px] font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  // Desktop variant
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex flex-col gap-4 p-4">
        {/* Title */}
        <div>
          <h3 className="text-[17px] font-semibold text-brand-black">Recortar imagen</h3>
          <p className="text-[13px] text-neutral-500 mt-1">Seleccioná el área que quieras recortar</p>
        </div>

        {/* Dimensions */}
        {imageLoaded && (
          <div className="flex items-center justify-center py-2 rounded-md bg-surface-elevated">
            <span className="text-[13px] font-medium text-neutral-500">{realWidth} × {realHeight} px</span>
          </div>
        )}

        {/* Aspect ratio options */}
        <div>
          <p className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide mb-2">Tamaño</p>
          <div className="flex flex-wrap gap-1.5">
            {ASPECT_OPTIONS.map((option, i) => (
              <button
                key={option.label}
                type="button"
                onClick={() => handleAspectChange(i)}
                className={`h-8 px-3.5 rounded-md text-[13px] font-medium transition-colors ${
                  selectedAspect === i
                    ? 'bg-brand-primary text-white'
                    : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'
                }`}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              onClick={handleReset}
              className="h-8 w-8 rounded-md bg-surface-card text-neutral-500 border border-neutral-200 flex items-center justify-center transition-colors hover:bg-surface-elevated"
              aria-label="Restablecer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom actions */}
      <div className="flex-shrink-0 p-4 pt-3 border-t border-neutral-200 space-y-2">
        <button
          type="button"
          onClick={handleCropAndDownload}
          disabled={!imageLoaded}
          className="w-full flex items-center justify-center gap-2 h-11 rounded-md text-[15px] font-medium bg-brand-primary text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-40"
        >
          <Download className="w-4 h-4" />
          Descargar
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="w-full flex items-center justify-center h-11 rounded-md text-[15px] font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

// Keep default export for backward compat (unused now, but safe)
const ImageCropModal: React.FC<{ imageUrl: string; fileName: string; onClose: () => void }> = () => null;
export default ImageCropModal;
